import { skipToken } from '@reduxjs/toolkit/query';
import { useCoinMetricsApiKey, useGetExchangeAssetMetricsCatalogQuery } from '@src/providers/CoinMetricsApiProvider';
import type { ExchangeAssetMetricCatalogEntry } from '@src/providers/CoinMetricsApiProvider/coinMetrics.types';
import { useMemo } from 'react';
import { CM_MAX_PAGE_SIZE } from './tokens';
import { isExchangeAssetPairFor } from './utils';

interface UseExchangeAssetCatalogParams {
  /** CoinMetrics asset IDs to resolve exchange combos for (e.g. ['btc', 'eth']). Must be a stable reference — inline arrays cause unnecessary re-runs. */
  cmAssetIds: string[];
  /** The metric that must be supported by each resolved exchange-asset pair. */
  metric: string;
}

interface UseExchangeAssetCatalogResult {
  /** Resolved exchange-asset strings (e.g. ['binance-btc', 'bybit-btc']). Sorted alphabetically. */
  exchangeAssets: string[];
  isLoading: boolean;
  isError: boolean;
  /** True when the catalog hit the pagination cap, so `exchangeAssets` may be missing entries. */
  isTruncated: boolean;
}

const EMPTY_EXCHANGE_ASSETS: string[] = [];

/**
 * Resolves which exchange-asset pairs (e.g. 'binance-btc') are available in the CoinMetrics
 * catalog for the given asset IDs and metric.
 *
 * Queries the catalog by metric alone (no exchange_assets filter) so the per-metric result is
 * cached once and reused across asset selections, then filters client-side to keep only pairs
 * matching the requested asset IDs. The catalog-v2 API does support an exchange_assets wildcard
 * filter, but scoping the query to the asset would fragment the RTK Query cache per selection.
 */
export function useExchangeAssetCatalog({
  cmAssetIds,
  metric,
}: UseExchangeAssetCatalogParams): UseExchangeAssetCatalogResult {
  const apiKey = useCoinMetricsApiKey();

  const hasRequiredParams = cmAssetIds.length > 0 && metric !== '';
  const queryArg = hasRequiredParams && apiKey ? { metrics: metric, page_size: CM_MAX_PAGE_SIZE } : skipToken;

  const { currentData, isLoading, isUninitialized, isError } = useGetExchangeAssetMetricsCatalogQuery(queryArg);
  const exchangeAssets = useMemo(() => extractExchangeAssets(currentData?.data, cmAssetIds), [currentData, cmAssetIds]);

  return {
    exchangeAssets,
    isLoading: isLoading || isUninitialized,
    isError,
    isTruncated: currentData?.truncated === true,
  };
}

/**
 * Extracts and optionally filters exchange_asset strings from a catalog response.
 * When `cmAssetIds` is provided and non-empty, only pairs whose asset matches one of the IDs
 * are returned (e.g. ['btc'] keeps 'binance-btc' but drops 'binance-eth').
 * Exported for direct unit testing — the hook is a thin RTK Query wrapper around this.
 */
export function extractExchangeAssets(
  entries: ExchangeAssetMetricCatalogEntry[] | undefined,
  cmAssetIds: string[] = []
): string[] {
  if (!entries || entries.length === 0) {
    return EMPTY_EXCHANGE_ASSETS;
  }
  const filtered =
    cmAssetIds.length > 0
      ? entries.filter(e => cmAssetIds.some(id => isExchangeAssetPairFor(e.exchange_asset, id)))
      : entries;
  return filtered.map(e => e.exchange_asset).toSorted();
}
